/**
 * مزود البيانات المشتركة
 * يحمل البيانات الأساسية مرة واحدة ويوفرها لجميع الصفحات
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getCachedData, setCachedData, clearAllCacheOnLogout } from '../utils/dataCache';

// دالة للحصول على رابط الـ Backend
const getBackendUrl = () => {
  return process.env.REACT_APP_BACKEND_URL || '';
};

const SharedDataContext = createContext(null);

export const useSharedData = () => {
  const context = useContext(SharedDataContext);
  if (!context) {
    throw new Error('useSharedData must be used within SharedDataProvider');
  }
  return context;
};

export const SharedDataProvider = ({ children }) => {
  const BACKEND_URL = getBackendUrl();
  
  // البيانات المشتركة
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // جلب الفروع
  const fetchBranches = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = getCachedData('branches');
      if (cached) {
        setBranches(cached);
        return cached;
      }
    }
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/branches`, { withCredentials: true });
      const data = response.data || [];
      setBranches(data);
      setCachedData('branches', data);
      return data;
    } catch (error) {
      console.error('Error fetching branches:', error);
      return [];
    }
  }, [BACKEND_URL]);

  // جلب الأقسام
  const fetchDepartments = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = getCachedData('departments');
      if (cached) {
        setDepartments(cached);
        return cached;
      }
    }
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/departments`, { withCredentials: true });
      const data = response.data || [];
      setDepartments(data);
      setCachedData('departments', data);
      return data;
    } catch (error) {
      console.error('Error fetching departments:', error);
      return [];
    }
  }, [BACKEND_URL]);

  // جلب الموظفين
  const fetchEmployees = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = getCachedData('employees');
      if (cached) {
        setEmployees(cached);
        return cached;
      }
    }
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/employees`, { withCredentials: true });
      const data = response.data || [];
      setEmployees(data);
      setCachedData('employees', data);
      return data;
    } catch (error) {
      console.error('Error fetching employees:', error);
      return [];
    }
  }, [BACKEND_URL]);

  // تحميل جميع البيانات المشتركة
  const loadAllSharedData = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    try {
      await Promise.all([
        fetchBranches(forceRefresh),
        fetchDepartments(forceRefresh),
        fetchEmployees(forceRefresh)
      ]);
      setInitialized(true);
    } catch (error) {
      console.error('Error loading shared data:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchBranches, fetchDepartments, fetchEmployees]);

  // تحديث بيانات محددة
  const refreshData = useCallback(async (dataType) => {
    switch (dataType) {
      case 'branches':
        return fetchBranches(true);
      case 'departments':
        return fetchDepartments(true);
      case 'employees':
        return fetchEmployees(true);
      case 'all':
        return loadAllSharedData(true);
      default:
        console.warn(`Unknown data type: ${dataType}`);
    }
  }, [fetchBranches, fetchDepartments, fetchEmployees, loadAllSharedData]);

  // مسح الكاش عند تسجيل الخروج
  const clearOnLogout = useCallback(() => {
    clearAllCacheOnLogout();
    setBranches([]);
    setDepartments([]);
    setEmployees([]);
    setInitialized(false);
  }, []);

  // دوال مساعدة للحصول على الأسماء
  const getBranchName = useCallback((branchId, lang = 'ar') => {
    const branch = branches.find(b => b.branch_id === branchId);
    if (!branch) return '-';
    return lang === 'ar' ? (branch.name_ar || branch.name) : branch.name;
  }, [branches]);

  const getDepartmentName = useCallback((deptId, lang = 'ar') => {
    const dept = departments.find(d => d.department_id === deptId);
    if (!dept) return '-';
    return lang === 'ar' ? (dept.name_ar || dept.name) : dept.name;
  }, [departments]);

  const getEmployeeName = useCallback((userId, lang = 'ar') => {
    const emp = employees.find(e => e.user_id === userId);
    if (!emp) return '-';
    return lang === 'ar' ? (emp.full_name_ar || emp.full_name) : emp.full_name;
  }, [employees]);

  const value = {
    // البيانات
    branches,
    departments,
    employees,
    loading,
    initialized,
    
    // دوال التحميل
    loadAllSharedData,
    refreshData,
    fetchBranches,
    fetchDepartments,
    fetchEmployees,
    clearOnLogout,
    
    // دوال مساعدة
    getBranchName,
    getDepartmentName,
    getEmployeeName
  };

  return (
    <SharedDataContext.Provider value={value}>
      {children}
    </SharedDataContext.Provider>
  );
};

export default SharedDataContext;
